import { QRCodeSVG } from "qrcode.react";

interface Props {
  /** The text the code encodes — usually a URL. */
  value: string;
  /** Accessible name; also what a screen reader announces in place of the graphic. */
  label: string;
  /** Rendered edge length in px, excluding the quiet zone. */
  size?: number;
}

/**
 * The app's only importer of the QR library (anti-corruption layer).
 *
 * Everything else asks for `<QrCode value label />` and stays ignorant of which
 * package draws it, so swapping the library is a one-file change. The white
 * frame is deliberate rather than themed: a QR rendered dark-on-dark in the
 * app's dark theme is invisible to a phone camera, so the contrast a scanner
 * needs is pinned here instead of left to whatever surface hosts it.
 */
function QrCode({ value, label, size = 148 }: Props) {
  return (
    <div className="inline-flex rounded-card bg-white p-2" role="img" aria-label={label}>
      <QRCodeSVG value={value} size={size} level="M" marginSize={0} aria-hidden="true" />
    </div>
  );
}

/**
 * Load a rendered <QrCode>'s SVG as an image drawn at `pixelSize` px.
 *
 * The clone is resized BEFORE it is rasterised, so a poster gets a vector-sharp
 * code at print size instead of the on-screen 208 px blown up — scaling a
 * bitmap of a ~29-module grid by a non-integer factor makes the modules
 * unequal widths, which is exactly what a phone camera trips on.
 */
async function qrSvgToImage(svg: SVGSVGElement, pixelSize: number): Promise<HTMLImageElement> {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("width", String(pixelSize));
  clone.setAttribute("height", String(pixelSize));
  const markup = new XMLSerializer().serializeToString(clone);
  const svgUrl = URL.createObjectURL(new Blob([markup], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("QR image failed to load"));
      img.src = svgUrl;
    });
    // Decoded while the blob URL is still alive, so revoking it below is safe.
    await img.decode().catch(() => {});
    return img;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

/**
 * Save a rendered <QrCode>'s SVG as a PNG, for printing on a flyer or poster.
 *
 * Rasterised at `scale`× (~1000 px for the share modal's code) on a white
 * canvas with a quiet zone —
 * the SVG itself is drawn with `marginSize={0}` (the on-screen frame supplies
 * the margin), and a code printed edge-to-edge on a coloured flyer won't scan.
 */
async function saveQrCodeAsPng(svg: SVGSVGElement, fileName: string, scale = 4): Promise<void> {
  // The rendered pixel size, not the viewBox (which counts modules, ~29 units).
  const size = svg.width.baseVal.value;
  const margin = Math.round(size * 0.1);
  const img = await qrSvgToImage(svg, size * scale);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = (size + margin * 2) * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2D canvas context");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, margin * scale, margin * scale, size * scale, size * scale);
  const png = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("PNG encode failed"))), "image/png"),
  );
  const a = document.createElement("a");
  a.href = URL.createObjectURL(png);
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 0);
}

export { QrCode, qrSvgToImage, saveQrCodeAsPng };
